import L from 'leaflet';
import 'leaflet.markercluster';
import type { AudioSource, AudioSourceType } from '../types.ts';

// ─── Custom pulsing HTML marker ───────────────────────────────────────────────

function createStationIcon(active: boolean, type: AudioSourceType = 'radio'): L.DivIcon {
  const typeClass = `station-dot--type-${type}`;
  const cls = active
    ? `station-dot ${typeClass} station-dot--active`
    : `station-dot ${typeClass}`;
  return L.divIcon({
    className: '',
    html: `<div class="${cls}"><div class="station-dot__ring"></div></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    tooltipAnchor: [10, 0],
  });
}

// ─── Map component ────────────────────────────────────────────────────────────

export class WorldMap {
  private map: L.Map;
  private markerLayer: L.MarkerClusterGroup;
  private markerIndex = new Map<string, L.Marker>();
  private typeIndex   = new Map<string, AudioSourceType>();
  private activeMarkerId: string | null = null;
  private onSelectCallback: ((source: AudioSource) => void) | null = null;

  constructor(containerId: string) {
    // ── Initialise Leaflet map ───────────────────────────────────────────────
    this.map = L.map(containerId, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      maxZoom: 14,
      zoomControl: false,
      attributionControl: true,
    });

    // ── Atlas-style tile layer (CartoDB Voyager — cartographic, no API key) ──
    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
        className: 'map-tile-layer',
      },
    ).addTo(this.map);

    // ── Zoom control — bottom-right ──────────────────────────────────────────
    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // ── Marker cluster group with custom styles ──────────────────────────────
    this.markerLayer = (L as unknown as { markerClusterGroup: (opts: unknown) => L.MarkerClusterGroup })
      .markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        const count = cluster.getChildCount();
        const size = count > 50 ? 'lg' : count > 10 ? 'md' : 'sm';
        return L.divIcon({
          html: `<div class="cluster-dot cluster-dot--${size}"><span>${count}</span></div>`,
          className: '',
          iconSize: [40, 40],
          iconAnchor: [20, 20],
        });
      },
    });
    this.map.addLayer(this.markerLayer);
  }

  /** Register a callback invoked when the user selects a station marker */
  onSelect(cb: (source: AudioSource) => void): this {
    this.onSelectCallback = cb;
    return this;
  }

  /** Replace all station markers with a new list */
  setStations(sources: AudioSource[]): void {
    this.markerLayer.clearLayers();
    this.markerIndex.clear();
    this.typeIndex.clear();

    for (const src of sources) {
      const marker = L.marker([src.lat, src.lng], {
        icon: createStationIcon(src.id === this.activeMarkerId, src.type),
        title: src.title,
      });

      marker.bindTooltip(
        `<div class="marker-tooltip">
          <strong>${src.title}</strong>
          <span>${src.country}</span>
        </div>`,
        { direction: 'right', offset: [10, 0], opacity: 1 },
      );

      marker.on('click', () => {
        this.setActive(src.id);
        this.onSelectCallback?.(src);
      });

      this.markerLayer.addLayer(marker);
      this.markerIndex.set(src.id, marker);
    }
  }

  /** Highlight the marker for the playing station */
  setActive(id: string): void {
    // Reset previous active marker icon
    if (this.activeMarkerId && this.activeMarkerId !== id) {
      const prev = this.markerIndex.get(this.activeMarkerId);
      if (prev) prev.setIcon(createStationIcon(false));
    }
    this.activeMarkerId = id;
    const marker = this.markerIndex.get(id);
    if (marker) marker.setIcon(createStationIcon(true));
  }

  /** Smoothly fly the map to focus on a lat/lng */
  flyTo(lat: number, lng: number, zoom = 5): void {
    this.map.flyTo([lat, lng], zoom, { duration: 1.8, easeLinearity: 0.2 });
  }

  /** Return the current map centre */
  getCenter(): { lat: number; lng: number } {
    const c = this.map.getCenter();
    return { lat: c.lat, lng: c.lng };
  }

  /** Expose the raw Leaflet map instance for external listeners */
  get leaflet(): L.Map {
    return this.map;
  }
}
